import { VisibilityOffOutlined, VisibilityOutlined } from "@mui/icons-material";
import {
  Box,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Formik } from "formik";
import React, { useState } from "react";
import FormButtons from "./FormButtons";
import SnackbarComponent from "components/SnackbarComponent";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import * as yup from "yup";
import { loginUser } from "reduxStore/actions/authActions";
import { setSnackbar } from "reduxStore/state";

const loginSchema = yup.object().shape({
  email: yup.string().email("invalid email").required("required"),
  password: yup.string().required("required"),
});

function LoginForm({ setPageType }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { palette } = useTheme();
  const primaryMain = palette.primary.main;
  const primaryLight = palette.primary.light;

  const isNonMobile = useMediaQuery("(min-width:600px)");

  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Function to toggle the password visibility
  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  // Function to handle mouse down event on the icon button
  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  // Password requirements message
  const passwordTooltip =
    "Password must be 5-25 characters long and include at least one uppercase letter, one lowercase letter, one digit, and one of the following special characters: @$!%*?&-";

  const handleLogin = async (values) => {
    setIsLoading(true);

    try {
      const loggedInUser = await dispatch(loginUser(values));

      if (loggedInUser.firstTimeLogin) {
        // Redirect to the password reset page if first-time login
        navigate("/password-reset-sent");

        setIsLoading(false);
      } else if (loggedInUser.message) {
        setIsLoading(false);

        dispatch(
          setSnackbar({
            open: true,
            message: loggedInUser.message,
            severity: "error",
          })
        );
      } else {
        setIsLoading(false);
        navigate("/home");
      }
    } catch (error) {
      setIsLoading(false);
      console.error(error);

      dispatch(
        setSnackbar({
          open: true,
          message: error.message,
          severity: "error",
        })
      );
    }
  };

  return (
    <Box>
      <Formik
        initialValues={{ email: "", password: "" }}
        validationSchema={loginSchema}
        onSubmit={handleLogin}
      >
        {({ values, errors, touched, handleChange, handleSubmit }) => (
          <form onSubmit={handleSubmit}>
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
            >
              <TextField
                label="Email"
                name="email"
                value={values.email}
                onChange={handleChange}
                error={errors.email && touched.email}
                helperText={errors.email && touched.email && errors.email}
                variant="outlined"
                margin="normal"
                sx={{ width: isNonMobile ? "50%" : "100%" }}
              />
              <Tooltip title={passwordTooltip} arrow placement="right-start">
                <TextField
                  label="Password"
                  name="password"
                  value={values.password}
                  onChange={handleChange}
                  error={errors.password && touched.password}
                  helperText={
                    errors.password && touched.password && errors.password
                  }
                  variant="outlined"
                  margin="normal"
                  sx={{ width: isNonMobile ? "50%" : "100%" }}
                  type={showPassword ? "text" : "password"}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={handleClickShowPassword}
                          onMouseDown={handleMouseDownPassword}
                          edge="end"
                          sx={{
                            backgroundColor: "transparent",
                            paddingRight: "1rem",
                          }}
                        >
                          {showPassword ? (
                            <VisibilityOffOutlined />
                          ) : (
                            <VisibilityOutlined />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Tooltip>
              <FormButtons isLoading={isLoading} isNonMobile={isNonMobile} />
              <Box mt="1rem">
                <Typography
                  onClick={() => setPageType("forgotPassword")}
                  sx={{
                    textDecoration: "underline",
                    color: primaryMain,
                    "&:hover": {
                      cursor: "pointer",
                      color: primaryLight,
                    },
                  }}
                >
                  Forgot Password?
                </Typography>
              </Box>
            </Box>
          </form>
        )}
      </Formik>
      <SnackbarComponent />
    </Box>
  );
}

export default LoginForm;
